import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import FormGroup from 'components/form/FormGroup';
import PagePreview from 'components/editor/PagePreview';
import Button from 'components/Button';
import { FaEdit, FaRegSave, FaTimes } from 'react-icons/fa';

type DocumentPreviewProps = {
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  renderedContents: string;
  renderedContentsEn?: string;
  handleEditSubmit: () => void;
  name: string;
  nameEn?: string;
  number: string;
  isMassDoc: boolean;
  isMultilingual: boolean;
  creatorName: string;
  creatorNameEn?: string;
  content: string;
  contentEn: string;
  setValue: (field: 'rendered_contents' | 'rendered_contents_en', value: string) => void;
  canEdit?: boolean;
};

const DocumentPreview: FC<DocumentPreviewProps> = ({
  isEditMode,
  setIsEditMode,
  renderedContents,
  renderedContentsEn,
  handleEditSubmit,
  name,
  nameEn,
  number,
  isMassDoc,
  isMultilingual,
  creatorName,
  creatorNameEn,
  content,
  contentEn,
  setValue,
  canEdit = false,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="text-center">
        {isEditMode && canEdit && (
          <>
            <Button
              className="mb-4 mr-2"
              onClick={() => {
                handleEditSubmit();
                setIsEditMode(false);
              }}
            >
              <FaRegSave className="mr-2" />
              {t('docWizard.step4.saveDocChanges')}
            </Button>
            <Button
              btnType="danger"
              className="mb-4"
              onClick={() => {
                setValue('rendered_contents', content);
                if (isMultilingual) setValue('rendered_contents_en', contentEn);
                setIsEditMode(false);
              }}
            >
              <FaTimes className="mr-2" />
              {t('docWizard.step4.discardDocChanges')}
            </Button>
          </>
        )}
        {!isEditMode && canEdit && (
          <Button
            btnType="info"
            className="mb-4"
            onClick={() => setIsEditMode(true)}
          >
            <FaEdit className="mr-2" />
            {t('docWizard.step4.editDocBeforeSave')}
          </Button>
        )}
      </div>
      {isEditMode ? (
        <>
          <h4 className="text-center">{name}</h4>
          <FormGroup
            controlId="formGroupRenderedContents"
            type="wysiwyg-editor"
            name="rendered_contents"
            isRequired
            isHorizontal
            controlGrid={12}
            disableLocalization
          />
          {isMultilingual && (
            <>
              <hr className="mt-5 mb-4" />
              <h4 className="text-center">{nameEn}</h4>
              <FormGroup
                controlId="formGroupRenderedContentsEn"
                type="wysiwyg-editor"
                name="rendered_contents_en"
                isRequired
                isHorizontal
                controlGrid={12}
                disableLocalization
              />
            </>
          )}
        </>
      ) : (
        <>
          <h4 className="text-center">{name}</h4>
          {isMultilingual && (
            <div className="text-center text-italic">({t('docWizard.step4.multilingualHintText')})</div>
          )}
          <PagePreview
            content={renderedContents}
            name={creatorName}
            number={number}
            isMassDoc={isMassDoc}
          />
          {isMultilingual && (
            <>
              <hr className="mt-5 mb-4" />
              <h4 className="text-center">{nameEn}</h4>
              <PagePreview
                content={renderedContentsEn}
                name={creatorNameEn}
                number={number}
                isMassDoc={isMassDoc}
                isMultilingual={isMultilingual}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default DocumentPreview;
